<template>
  <AudioGrid
    v-if="audioData && audioData.length"
    :audio-data="audioData"
    :page="page"
    :tag="tag"
    :tag-name="getTagName"
    :total-page-count="pageCount"
  ></AudioGrid>
</template>

<script setup lang="ts">
import AudioGrid from '@/components/audio/AudioGrid.vue';
import { audioSectionQuery } from '@/queries/GetAudioSection';
import { BootStrap } from '@/types/global.interface';
import { computed, inject, ref } from 'vue';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import { NameSlug } from '@/types/common.interface';
import { PageTag } from '../../types/audio.interface';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/stores/mainStore.js';
import { useQuery } from 'villus';

const props = withDefaults(
  defineProps<PageTag>(),
  { page: 1 }
);

const bootstrap = inject<BootStrap>('bootstrap');

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const store = useMainStore();
const { useBootstrap } = storeToRefs(store);

const audioData = ref([]);
const limit = 27;
const shouldSkip = ref(false);
const total = ref(0);

const getTagName = computed(() => {
  if (audioData.value.length) {
    const selected = audioData.value[0].tags.nodes.filter(
      (tag: NameSlug) => props.tag === tag.slug
    );

    return selected.length ? selected[0].name : '';
  }

  return '';
});
const pageCount = computed(() => Math.ceil(total.value / limit));

const queryVariables = {
  limit: limit,
  paged: +props.page,
  subType: props.tag ? 'post_tag' : 'all_audio',
  subTypeValue: props.tag || null,
  tag: props.tag || null,
  type: 'audio',
};

const { data } = useQuery({
  onData: data => {
    if (data && Object.keys(data).length) {
      if (data.allAudio.nodes && data.allAudio.nodes.length) {
        audioData.value = data.allAudio.nodes;
        total.value = data.totalCount;
        setMetaTitle('Audio');
        setMetaDescription();
        dataLoaded();
      }

      if (!data.allAudio.nodes.length) {
        handleError('AudioSection', `Page ${props.page}, limit ${limit}, tag ${props.tag}`);
      }
    }
  },
  query: audioSectionQuery,
  skip: isNaN(props.page) || shouldSkip,
  variables: queryVariables,
});

if (isNaN(props.page)) {
  handleError('AudioSection', `Page ${props.page}, limit ${limit}, tag ${props.tag}`);
}

if (isNaN(props.page)) {
  handleError('AudioSection', `Page ${props.page}, limit ${limit}, tag ${props.tag}`);
}

if (useBootstrap.value) {
  shouldSkip.value = true;

  if (
    bootstrap &&
    bootstrap.initial.main.posts &&
    Object.keys(bootstrap.initial.main.posts).length
  ) {
    audioData.value = bootstrap.initial.main.posts;
    total.value = bootstrap.initial.main.pagination.total_posts;
    setMetaTitle('Audio');
    setMetaDescription();
    dataLoaded();
  }

  if (
    bootstrap &&
    (!bootstrap.initial.main.posts || Object.keys(bootstrap.initial.main.posts).length === 0)
  ) {
    shouldSkip.value = false;
  }
}

if (!useBootstrap.value) {
  shouldSkip.value = false;
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: audioSectionQuery,
          variables: {
            limit: 27,
            paged: params.page ? +params.page : 1,
            subType: params.tag ? 'post_tag' : 'all_audio',
            subTypeValue: params.tag || null,
            tag: params.tag || null,
            type: 'audio',
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>
